import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  Chip,
  IconButton,
  Link,
  Button,
  useMediaQuery
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { fetchPublicJobs } from 'src/redux/jobs'
import { useHandleChangeRoute } from 'data/hooks'

const JobCarousel = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const handleChangeRoute = useHandleChangeRoute()
  const { jobsList, isLoadingJobs } = useSelector(state => state.jobs)
  const [currentIndex, setCurrentIndex] = useState(0)
  const carouselRef = React.useRef(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    // Fetch 50 random jobs from partner employers only
    const searchParams = {
      pageSize: 50,
      page: 1,
    }
    dispatch(fetchPublicJobs(searchParams))
  }, [dispatch])

  // Removed auto-scroll functionality - users must click arrows to navigate

  const goToPrevious = () => {
    const maxIndex = isMobile ? jobsList.length - 1 : jobsList.length - 3
    setCurrentIndex(prevIndex =>
      prevIndex <= 0 ? Math.max(0, maxIndex) : prevIndex - 1
    )
  }

  const goToNext = () => {
    const maxIndex = isMobile ? jobsList.length - 1 : jobsList.length - 3
    setCurrentIndex(prevIndex =>
      prevIndex >= maxIndex ? 0 : prevIndex + 1
    )
  }

  const getLocation = (job) => {
    if (job.city && job.state) {
      return `${job.city}, ${job.state}`
    } else if (job.state) {
      return job.state
    }
    return 'Location TBD'
  }

  const getSpecialtyInfo = (job) => {
    const specialties = []

    if (job.specialties && job.specialties.length > 0) {
      specialties.push(...job.specialties.map(s => s.name))
    }

    if (job.subspecialties && job.subspecialties.length > 0) {
      specialties.push(...job.subspecialties.map(s => s.name))
    }

    return specialties.length > 0 ? specialties.join(', ') : null
  }

  if (isLoadingJobs || !jobsList || jobsList.length === 0) {
    return null
  }

  return (
    <Box sx={{ mt: { xs: 4, sm: 6 }, px: { xs: 0, sm: 2 } }}>
      {/* Carousel Container */}
      <Box sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        minHeight: '280px', // Ensure enough height for cards (240px + padding)
        py: 2 // Add some vertical padding
      }}>
        {/* Left Arrow */}
        <IconButton
          onClick={goToPrevious}
          sx={{
            position: 'absolute',
            left: { xs: 10, sm: 10 }, // Position within expanded container on mobile
            zIndex: 2,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            '&:hover': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.dark
            }
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: '18px' }} />
        </IconButton>

        <Box ref={carouselRef} sx={{
          position: 'relative',
          overflow: 'hidden',
          flex: 1,
          mx: { xs: 3, sm: 4 },
          minHeight: '260px' // Ensure inner container has enough height
        }}>
          <Box
            sx={{
              display: 'flex',
              transition: 'transform 0.5s ease-in-out',
              transform: isMobile
                ? `translateX(calc(50% - 140px - ${currentIndex * 300}px))` // Mobile: center first card (50% - half card width of 280px)
                : `translateX(-${currentIndex * 370}px)`, // Desktop: card width + gap (350px + 20px)
              gap: 2.5,
              alignItems: 'stretch', // Ensure cards stretch to full height
              minHeight: '240px' // Match card height
            }}
          >
            {jobsList.map((job, index) => (
              <Link
                key={`${job.id}-${index}`}
                href={`/jobs/${job.id}`}
                target='_blank'
                sx={{
                  color: 'inherit',
                  textDecoration: 'none !important',
                  background: 'none !important',
                  backgroundSize: '0 !important',
                  display: 'inline-block',
                  '&:hover': {
                    textDecoration: 'none !important',
                    cursor: 'pointer',
                    background: 'none !important',
                    backgroundSize: '0 !important',
                  },
                  '&:focus': {
                    textDecoration: 'none !important',
                    background: 'none !important',
                    backgroundSize: '0 !important',
                  },
                }}
              >
                <Card
                  sx={{
                    width: { xs: 'calc(100vw - 120px)', sm: '350px' }, // Even more margin on mobile to prevent cutoff
                    maxWidth: { xs: '280px', sm: '350px' }, // Smaller max width on mobile for safe margins
                    flexShrink: 0,
                    height: '240px', // Increased height for better content display
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)', // Softer, more subtle shadow
                    WebkitBackfaceVisibility: 'hidden',
                    backfaceVisibility: 'hidden',
                    transform: 'translateZ(0)',
                    '&:hover': {
                      transform: 'translateY(-2px) translateZ(0)', // Reduced hover movement
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)', // Softer hover shadow
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  {/* Location */}
                  <Box sx={{ mb: 0.75 }}>
                    <Chip
                      label={getLocation(job)}
                      icon={<LocationOnIcon sx={{ fontSize: '12px' }} />}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.dark,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: '22px'
                      }}
                    />
                  </Box>

                  {/* Job Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.grey[900],
                      mb: 0.4,
                      fontSize: '0.9rem',
                      lineHeight: 1.15,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {job.title}
                  </Typography>

                  {/* Candidate Type */}
                  {job.candidateType?.name && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.secondary.main,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        mb: 0.3,
                        lineHeight: 1.1
                      }}
                    >
                      {job.candidateType.name}
                    </Typography>
                  )}

                  {/* Specialty & Subspecialty */}
                  {getSpecialtyInfo(job) && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.grey[900],
                        fontSize: '0.7rem',
                        mb: 0.3,
                        lineHeight: 1.1,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {getSpecialtyInfo(job)}
                    </Typography>
                  )}

                  {/* Employer Name - Directly under specialty */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.grey[900],
                      fontSize: '0.75rem',
                      lineHeight: 1.1
                    }}
                  >
                    {job.employerName || 'No Employer Name'}
                  </Typography>
                </CardContent>
              </Card>
              </Link>
            ))}
          </Box>
        </Box>

        {/* Right Arrow */}
        <IconButton
          onClick={goToNext}
          sx={{
            position: 'absolute',
            right: { xs: 10, sm: 10 }, // Position within expanded container on mobile
            zIndex: 2,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            '&:hover': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.dark
            }
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </Box>

      {/* Call to Action */}
      <Box sx={{ textAlign: 'center', mt: { xs: 6, sm: 8 }, px: { xs: 2, sm: 0 } }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleChangeRoute('/jobs')}
          sx={{
            borderRadius: '50px',
            px: { xs: 4, sm: 6 },
            py: { xs: 2, sm: 3 },
            fontSize: { xs: '1rem', sm: '1.2rem' },
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            boxShadow: theme.palette.shadows.colored.primary,
            textTransform: 'none',
            minWidth: { xs: '250px', sm: '280px' },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '320px', sm: 'none' },
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: theme.palette.shadows.colored.primaryHover,
              transform: 'translateY(-2px)',
            }
          }}
        >
          🔍 Search All Job Openings
        </Button>
      </Box>
    </Box>
  )
}

export default JobCarousel
